/* FreeShop Duty-Free Premium — behaviour layer (vanilla, no deps) */
(() => {
  'use strict';
  const TZ = 'Europe/Bucharest';
  const pad = (n) => String(n).padStart(2, '0');

  /** Current wall-clock parts in Romania, regardless of visitor timezone. */
  function nowInTZ() {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: TZ, hour12: false, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', weekday: 'short',
    }).formatToParts(new Date());
    const o = {};
    parts.forEach((p) => { o[p.type] = p.value; });
    return { h: +o.hour % 24, m: +o.minute, s: +o.second, wd: o.weekday };
  }

  /** Seconds remaining for a countdown element. Returns null when not applicable. */
  function remaining(el) {
    const mode = el.dataset.mode || 'daily';
    if (mode === 'date') {
      const end = Date.parse(el.dataset.end || '');
      if (Number.isNaN(end)) return null;
      return Math.max(0, Math.floor((end - Date.now()) / 1000));
    }
    const t = nowInTZ();
    const elapsed = t.h * 3600 + t.m * 60 + t.s;
    if (mode === 'cutoff') {
      const cutoff = (parseInt(el.dataset.cutoff, 10) || 15) * 3600;
      const weekend = t.wd === 'Sat' || t.wd === 'Sun';
      if (weekend || elapsed >= cutoff) return null;
      return cutoff - elapsed;
    }
    return 86400 - elapsed; // daily: until midnight Romania time
  }

  function renderCountdown(el) {
    const secs = remaining(el);
    const wrap = el.closest('[data-df-hide-on-end]') || el;
    if (secs === null || secs <= 0) {
      const after = el.dataset.afterLabel;
      if (after) {
        // e.g. after the dispatch cut-off: keep the bar, swap to a truthful message without a clock
        el.querySelectorAll('[data-df-clock],[data-df-label]').forEach((n) => { n.hidden = true; });
        let msg = el.querySelector('[data-df-after]');
        if (!msg) { msg = document.createElement('span'); msg.setAttribute('data-df-after', ''); el.appendChild(msg); }
        msg.textContent = after;
        wrap.hidden = false;
      } else if (el.dataset.endText) {
        const clock = el.querySelector('[data-df-clock]');
        if (clock) clock.textContent = el.dataset.endText;
      } else {
        wrap.hidden = true;
      }
      return;
    }
    wrap.hidden = false;
    if (el.dataset.afterLabel) {
      el.querySelectorAll('[data-df-clock],[data-df-label]').forEach((n) => { n.hidden = false; });
      const msg = el.querySelector('[data-df-after]');
      if (msg) msg.remove();
    }
    const d = Math.floor(secs / 86400);
    const h = Math.floor((secs % 86400) / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    const set = (sel, v) => el.querySelectorAll(sel).forEach((n) => { n.textContent = v; });
    const totalH = d * 24 + h;
    const hasDays = !!el.querySelector('[data-df-d]');
    set('[data-df-d]', pad(d));
    set('[data-df-h]', pad(hasDays ? h : totalH));
    set('[data-df-m]', pad(m));
    set('[data-df-s]', pad(s));
    set('[data-df-clock]', `${pad(totalH)}:${pad(m)}:${pad(s)}`);
    set('[data-df-hm]', totalH > 0 ? `${totalH}h ${pad(m)}m` : `${m}m ${pad(s)}s`);
  }

  function initCountdowns(root = document) {
    const els = [...root.querySelectorAll('[data-df-countdown]')];
    if (!els.length) return;
    const tick = () => els.forEach(renderCountdown);
    tick();
    setInterval(tick, 1000);
  }

  /** Estimated delivery window in Romanian, skipping weekends. */
  function initDelivery(root = document) {
    root.querySelectorAll('[data-df-delivery]').forEach((el) => {
      const min = parseInt(el.dataset.min, 10) || 1;
      const max = parseInt(el.dataset.max, 10) || 3;
      const cutoff = parseInt(el.dataset.cutoff, 10) || 15;
      const t = nowInTZ();
      const addBiz = (n) => {
        const d = new Date();
        if (t.h >= cutoff) n += 1;
        while (n > 0) {
          d.setDate(d.getDate() + 1);
          const day = d.getDay();
          if (day !== 0 && day !== 6) n -= 1;
        }
        return d;
      };
      const fmt = (d) => d.toLocaleDateString('ro-RO', { weekday: 'long', day: 'numeric', month: 'long', timeZone: TZ });
      const a = fmt(addBiz(min));
      const b = fmt(addBiz(max));
      el.textContent = a === b ? a : `${a} – ${b}`;
    });
  }

  /** Sticky add-to-cart bar on product pages. */
  function initSticky() {
    const bar = document.querySelector('[data-df-sticky]');
    if (!bar) return;
    const form = document.querySelector('product-form form[action*="/cart/add"]');
    const mainBtn = form && form.querySelector('[type="submit"]');
    if (!mainBtn) return;
    const priceTarget = bar.querySelector('[data-df-sticky-price]');
    const syncPrice = () => {
      const info = mainBtn.closest('.product__info-wrapper, .product__info-container') || document;
      const sale = info.querySelector('.price .price__sale .price-item--sale, .price .price-item--sale');
      const reg = info.querySelector('.price .price__regular .price-item--regular');
      const cmp = info.querySelector('.price .price__sale s .price-item--regular');
      const priceEl = info.querySelector('.price');
      const onSale = priceEl && priceEl.classList.contains('price--on-sale');
      if (!priceTarget) return;
      const p = (onSale ? sale : reg) || sale || reg;
      if (p) priceTarget.innerHTML = `${p.textContent.trim()}${onSale && cmp ? `<s>${cmp.textContent.trim()}</s>` : ''}`;
    };
    const stickyBtn = bar.querySelector('[data-df-sticky-btn]');
    const syncState = () => {
      if (!stickyBtn) return;
      stickyBtn.disabled = mainBtn.disabled;
      stickyBtn.querySelector('span').textContent = mainBtn.disabled ? (bar.dataset.soldOut || 'Stoc epuizat') : (bar.dataset.label || 'Adaugă în coș');
    };
    stickyBtn && stickyBtn.addEventListener('click', (e) => {
      e.preventDefault();
      if (mainBtn.disabled) return;
      mainBtn.click();
    });
    const io = new IntersectionObserver(([entry]) => {
      const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      bar.classList.toggle('is-visible', past);
      bar.setAttribute('aria-hidden', String(!past));
      if (past) { syncPrice(); syncState(); }
    }, { threshold: 0 });
    io.observe(mainBtn);
    new MutationObserver(() => { syncPrice(); syncState(); }).observe(form.closest('section') || document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['disabled'] });
  }

  function initReveal() {
    const els = document.querySelectorAll('.df-reveal');
    if (!('IntersectionObserver' in window)) { els.forEach((e) => e.classList.add('is-in')); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); } });
    }, { rootMargin: '0px 0px -8% 0px' });
    const vh = window.innerHeight || 800;
    els.forEach((e) => {
      // reveal immediately whatever is already on screen; animate the rest on scroll
      if (e.getBoundingClientRect().top < vh) e.classList.add('is-in'); else io.observe(e);
    });
    // safety net: never leave content hidden (e.g. print, bots, odd browsers)
    setTimeout(() => els.forEach((e) => e.classList.add('is-in')), 4000);
  }

  /** Lazy Google Maps iframe (loads only when the footer map scrolls into view). */
  function initMaps(root = document) {
    root.querySelectorAll('[data-df-map]:not([data-loaded])').forEach((box) => {
      const load = () => {
        if (box.dataset.loaded) return;
        box.dataset.loaded = '1';
        const f = document.createElement('iframe');
        f.src = box.dataset.src;
        f.title = 'Hartă locație';
        f.loading = 'lazy';
        f.referrerPolicy = 'no-referrer-when-downgrade';
        f.allowFullscreen = true;
        box.appendChild(f);
      };
      if (!('IntersectionObserver' in window)) return load();
      const io = new IntersectionObserver(([en]) => { if (en.isIntersecting) { load(); io.disconnect(); } }, { rootMargin: '400px 0px' });
      io.observe(box);
    });
  }

  /** Mega menu: touch support (first tap opens the panel) + Escape to close. */
  function initNav() {
    const items = document.querySelectorAll('.df-nav__item--panel');
    if (!items.length) return;
    const closeAll = (except) => items.forEach((i) => { if (i !== except) i.classList.remove('is-open'); });
    items.forEach((item) => {
      const link = item.querySelector('.df-nav__link');
      link.addEventListener('click', (e) => {
        const touch = window.matchMedia('(hover: none)').matches;
        if (touch && !item.classList.contains('is-open')) { e.preventDefault(); closeAll(item); item.classList.add('is-open'); }
      });
    });
    document.addEventListener('click', (e) => { if (!e.target.closest('.df-nav__item--panel')) closeAll(); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') { closeAll(); if (document.activeElement && document.activeElement.closest('.df-nav__panel')) document.activeElement.blur(); } });
    const m = location.hash.match(/^#df-nav-(\d)$/); // preview helper: #df-nav-1 opens the first panel
    if (m && items[m[1] - 1]) items[m[1] - 1].classList.add('is-open');
  }

  function init() { initCountdowns(); initDelivery(); initSticky(); initReveal(); initMaps(); initNav(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();

  // Theme editor support
  document.addEventListener('shopify:section:load', (e) => { initCountdowns(e.target); initDelivery(e.target); initReveal(); initMaps(e.target); });
})();
